// Bagged-peak celebrations for the flyover VIDEO renderer (lib/flyover_renderer/entry.ts).
//
// The pure half: where each summit sits on the flyover's timeline, and the choreography of an
// arrival — pin pop, pulse rings, the card on the summit, a yellow-and-white confetti burst. It is a
// straight port of the in-app 3D flyover (Android modules/hikePreview/peaks: FlyoverPeakPlacement,
// FlyoverPeakArrival, FlyoverPeakTimeline) so the video and the app celebrate a walk identically;
// keep the constants in step with those files.
//
// Everything is a function of numbers the renderer already has (coords, progress, clip time), so
// the whole thing is Jest-tested without a browser or a map.

/** One bagged summit, as Flyover::PeakMoments writes it into render_config.peaks. */
export interface FlyoverPeakConfig {
  id: string;
  name: string;
  lng: number;
  lat: number;
  elevationMeters?: number | null;
  firstBag?: boolean;
  listName?: string | null;
  listBaggedCount?: number | null;
  listPeaksCount?: number | null;
  badgeUrl?: string | null;
  accent?: string | null;
}

/** A summit pinned to the flyover's own timeline: `frac` is 0..1 of the distance flown. */
export interface PlacedPeak {
  peak: FlyoverPeakConfig;
  frac: number;
}

export type CelebrationTier = "big" | "small";

/** A summit farther than this from the flown line has no moment at which the puck reaches it. */
export const MAX_OFF_ROUTE_M = 400;
/** Near-equal projections are one pass; an out-and-back celebrates the summit on the way out. */
const SAME_PASS_TOLERANCE_M = 15;
const EARTH_RADIUS_M = 6371008.8;

// ---------------------------------------------------------------- placement

/** Projection of p onto edge a→b: [t in 0..1, metres off the edge], local equirectangular frame. */
export function projectOntoEdge(p: number[], a: number[], b: number[]): [number, number] {
  const mPerDegLat = (Math.PI / 180) * EARTH_RADIUS_M;
  const mPerDegLng = mPerDegLat * Math.cos((p[1] * Math.PI) / 180);
  const bx = (b[0] - a[0]) * mPerDegLng, by = (b[1] - a[1]) * mPerDegLat;
  const px = (p[0] - a[0]) * mPerDegLng, py = (p[1] - a[1]) * mPerDegLat;
  const len2 = bx * bx + by * by;
  const t = len2 <= 0 ? 0 : Math.min(Math.max((px * bx + py * by) / len2, 0), 1);
  return [t, Math.hypot(px - t * bx, py - t * by)];
}

/**
 * Where along the flown line each summit is reached, in walking order.
 *
 * Measured on the coords the renderer actually flies rather than taken from the server's
 * full-track distance — the video flies a decimated copy, and only a projection onto that copy
 * puts the moment where the puck is. `cumKm` is the renderer's own cumulative distance per vertex.
 */
export function placePeaks(peaks: FlyoverPeakConfig[] | null | undefined, coords: number[][], cumKm: number[]): PlacedPeak[] {
  if (!Array.isArray(peaks) || peaks.length === 0 || coords.length < 2) return [];
  const totalKm = cumKm[cumKm.length - 1] || 0;
  if (totalKm <= 0) return [];
  const placed: PlacedPeak[] = [];
  for (const peak of peaks) {
    if (!Number.isFinite(peak?.lng) || !Number.isFinite(peak?.lat)) continue;
    const p = [peak.lng, peak.lat];
    const candidates: [number, number][] = []; // [offRouteM, alongKm]
    for (let i = 0; i < coords.length - 1; i++) {
      const [t, off] = projectOntoEdge(p, coords[i], coords[i + 1]);
      candidates.push([off, cumKm[i] + t * (cumKm[i + 1] - cumKm[i])]);
    }
    const best = Math.min(...candidates.map((c) => c[0]));
    if (best > MAX_OFF_ROUTE_M) continue;
    const alongKm = Math.min(...candidates.filter((c) => c[0] <= best + SAME_PASS_TOLERANCE_M).map((c) => c[1]));
    placed.push({ peak, frac: Math.min(Math.max(alongKm / totalKm, 0), 1) });
  }
  return placed.sort((a, b) => a.frac - b.frac);
}

/** Cumulative great-circle distance (km) at each vertex of coords, for placePeaks. */
export function cumulativeKm(coords: number[][]): number[] {
  const out = [0];
  for (let i = 1; i < coords.length; i++) {
    const [lng1, lat1] = coords[i - 1], [lng2, lat2] = coords[i];
    const dLat = ((lat2 - lat1) * Math.PI) / 180, dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
    out.push(out[i - 1] + (2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(a)))) / 1000);
  }
  return out;
}

// ---------------------------------------------------------------- the ease into a summit

/** Playback rate on a summit itself — slowed, never stopped. */
export const SUMMIT_RATE = 0.5;
/** Half-width of the ease into and out of a summit, as a fraction of the route. */
export const DWELL_HALF_WIDTH = 0.035;

/** The playback-rate multiplier at progress t: 1 away from summits, SUMMIT_RATE on one. */
export function peakDwellScale(t: number, fracs: number[]): number {
  let s = 1;
  for (const f of fracs) {
    const d = Math.abs(t - f);
    if (d >= DWELL_HALF_WIDTH) continue;
    const closeness = 1 - d / DWELL_HALF_WIDTH;
    const smooth = closeness * closeness * (3 - 2 * closeness);
    s = Math.min(s, 1 - (1 - SUMMIT_RATE) * smooth);
  }
  return s;
}

/**
 * How much longer the body plays because of the summit dwells (≥ 1). The renderer fits the clip
 * into its 45s budget from the body duration, so it must know the dwells stretch it.
 */
export function dwellStretch(fracs: number[], steps = 2000): number {
  if (!fracs.length) return 1;
  let sum = 0;
  for (let i = 0; i < steps; i++) sum += 1 / peakDwellScale((i + 0.5) / steps, fracs);
  return sum / steps;
}

// ---------------------------------------------------------------- arrivals

/** Whole arrival: the card is gone and the pin back to its named resting state by now. */
export const ARRIVAL_MS = 3800;
const CARD_FADE_FROM_MS = 3450;
const CARD_FADE_IN_MS = 120;
export const CARD_POP_MS = 460;
export const PIN_POP_MS = 520;
export const PIN_POP_PEAK = 1.6;
const PIN_RISE_MS = 130;
export const PULSE_MS = 950;
const SECOND_PULSE_DELAY_MS = 320;
export const CONFETTI_MS = 1300;
export const CONFETTI_COUNT = 22;
const GRAVITY = 320;
/** When the next summit's card arrives, the one still up hands over and fades out this fast. */
export const CARD_HANDOVER_MS = 200;
/** Two first bags closer than this in clip time play the second small. */
export const BIG_COOLDOWN_MS = 2500;

/** The pin's pop: a quick swell, then a springy settle back to rest. */
export function pinScale(elapsedMs: number): number {
  if (elapsedMs <= 0 || elapsedMs >= PIN_POP_MS) return 1;
  if (elapsedMs < PIN_RISE_MS) {
    const p = elapsedMs / PIN_RISE_MS;
    return 1 + (PIN_POP_PEAK - 1) * (1 - (1 - p) * (1 - p));
  }
  const p = (elapsedMs - PIN_RISE_MS) / (PIN_POP_MS - PIN_RISE_MS);
  const damped = Math.exp(-4.5 * p) * Math.cos(p * Math.PI * 1.5);
  return 1 + (PIN_POP_PEAK - 1) * damped * (1 - p);
}

export interface Pulse { ring: number; radius: number; opacity: number }

/** Rings at elapsedMs, radius 0..1 of the full spread: one for a repeat bag, two for a first. */
export function pulses(elapsedMs: number, tier: CelebrationTier): Pulse[] {
  const starts = tier === "big" ? [0, SECOND_PULSE_DELAY_MS] : [0];
  const out: Pulse[] = [];
  starts.forEach((start, ring) => {
    const local = elapsedMs - start;
    if (local < 0 || local >= PULSE_MS) return;
    const p = local / PULSE_MS;
    out.push({ ring, radius: 1 - (1 - p) ** 3, opacity: 0.9 * (1 - p) * (1 - p) });
  });
  return out;
}

/** The card pops up from small, overshoots, and settles — "big to small" into place. */
export function cardScale(elapsedMs: number): number {
  if (elapsedMs < 0 || elapsedMs >= CARD_POP_MS) return 1;
  const p = elapsedMs / CARD_POP_MS;
  const c1 = 2.2, c3 = c1 + 1;
  const back = 1 + c3 * (p - 1) ** 3 + c1 * (p - 1) ** 2;
  return 0.55 + 0.45 * back;
}

export function cardOpacity(elapsedMs: number): number {
  if (elapsedMs < 0 || elapsedMs >= ARRIVAL_MS) return 0;
  if (elapsedMs < CARD_FADE_IN_MS) return elapsedMs / CARD_FADE_IN_MS;
  if (elapsedMs > CARD_FADE_FROM_MS) return 1 - (elapsedMs - CARD_FADE_FROM_MS) / (ARRIVAL_MS - CARD_FADE_FROM_MS);
  return 1;
}

/** Small deterministic PRNG, so a summit always throws the same burst. */
export function seededRandom(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Stable 32-bit hash of a peak id, to seed its confetti. */
export function hashSeed(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
  return h >>> 0;
}

export interface ConfettiBit { dx: number; dy: number; rotation: number; opacity: number; yellow: boolean }

/** The burst at elapsedMs, as offsets from the summit in app dp (scale to frame px at draw). */
export function confetti(elapsedMs: number, seed: number): ConfettiBit[] {
  if (elapsedMs < 0 || elapsedMs >= CONFETTI_MS) return [];
  const t = elapsedMs / 1000;
  const fade = elapsedMs < CONFETTI_MS * 0.6 ? 1 : 1 - (elapsedMs - CONFETTI_MS * 0.6) / (CONFETTI_MS * 0.4);
  const rnd = seededRandom(seed);
  const out: ConfettiBit[] = [];
  for (let i = 0; i < CONFETTI_COUNT; i++) {
    const angle = -Math.PI / 2 + (rnd() - 0.5) * Math.PI * 0.9;
    const speed = 110 + rnd() * 110;
    const spin = (rnd() - 0.5) * 900;
    const yellow = rnd() < 0.5;
    out.push({
      dx: Math.cos(angle) * speed * t,
      dy: Math.sin(angle) * speed * t + 0.5 * GRAVITY * t * t,
      rotation: spin * t,
      opacity: Math.min(Math.max(fade, 0), 1),
      yellow
    });
  }
  return out;
}

// ---------------------------------------------------------------- who celebrates when

export interface Arrival { index: number; tier: CelebrationTier; atMs: number }

/** A jump at least this far along the route is the viewer skipping (the live 1 km fast-forward). */
export const MAX_NATURAL_STEP_KM = 0.5;

/**
 * Whether progress moved from prevT to t by playing rather than by a skip: forward, and less than
 * MAX_NATURAL_STEP_KM. A summit passed by a skip is planted silently, never celebrated.
 */
export function isNaturalStep(prevT: number, t: number, totalKm: number): boolean {
  const stepKm = (t - prevT) * totalKm;
  return stepKm >= 0 && stepKm < MAX_NATURAL_STEP_KM;
}

/**
 * Tracks which summits the flyover has reached and starts each one's arrival on its clock (the
 * video's clip clock, or wall time in the live preview). A summit is celebrated when progress first
 * reaches it by playing; reached by a skip it is planted silently; skipped back past, it is
 * un-reached and its celebration dropped, so playing on through it celebrates it again. Two first
 * bags inside BIG_COOLDOWN_MS: the second plays small.
 */
export class PeakArrivals {
  private reached: boolean[];
  private lastBigAtMs: number | null = null;
  arrivals: Arrival[] = [];

  constructor(private readonly placed: PlacedPeak[]) {
    this.reached = placed.map(() => false);
  }

  /**
   * Feed the body progress at time clipMs; returns the arrivals that start now. `natural` is false
   * for a skip (see isNaturalStep): summits it lands past are planted without a celebration.
   */
  update(t: number, clipMs: number, natural = true): Arrival[] {
    const started: Arrival[] = [];
    this.placed.forEach((p, index) => {
      if (t < p.frac) {
        if (this.reached[index]) {
          this.reached[index] = false;
          this.arrivals = this.arrivals.filter((a) => a.index !== index);
        }
        return;
      }
      if (this.reached[index]) return;
      this.reached[index] = true;
      if (!natural) return;
      let tier: CelebrationTier = p.peak.firstBag ? "big" : "small";
      if (tier === "big") {
        if (this.lastBigAtMs != null && clipMs - this.lastBigAtMs < BIG_COOLDOWN_MS) tier = "small";
        else this.lastBigAtMs = clipMs;
      }
      const arrival = { index, tier, atMs: clipMs };
      this.arrivals.push(arrival);
      started.push(arrival);
    });
    return started;
  }

  /**
   * How visible a summit's card is at clipMs: its own pop-hold-fade, cut short when a later summit's
   * card arrives. Close-together summits (a ridge of tops) would otherwise stack card on card.
   */
  cardOpacityFor(index: number, clipMs: number): number {
    const own = this.activeFor(index, clipMs);
    if (!own) return 0;
    let opacity = cardOpacity(clipMs - own.atMs);
    const next = this.arrivals.find((a) => a.atMs > own.atMs || (a.atMs === own.atMs && a.index > own.index));
    if (next && clipMs >= next.atMs) {
      opacity = Math.min(opacity, Math.max(0, 1 - (clipMs - next.atMs) / CARD_HANDOVER_MS));
    }
    return opacity;
  }

  /** A replay from the start: nothing reached, nothing playing. */
  reset(): void {
    this.reached = this.placed.map(() => false);
    this.arrivals = [];
    this.lastBigAtMs = null;
  }

  /** Whether any celebration is still animating at clipMs (the live preview keeps repainting). */
  isAnimating(clipMs: number): boolean {
    return this.arrivals.some((a) => clipMs - a.atMs < ARRIVAL_MS);
  }

  isReached(index: number): boolean { return this.reached[index] === true; }
  reachedCount(): number { return this.reached.filter(Boolean).length; }
  /** The most recent arrival of a summit still playing at clipMs, if any. */
  activeFor(index: number, clipMs: number): Arrival | null {
    for (let i = this.arrivals.length - 1; i >= 0; i--) {
      const a = this.arrivals[i];
      if (a.index === index) return clipMs - a.atMs < ARRIVAL_MS ? a : null;
    }
    return null;
  }
}

// ---------------------------------------------------------------- the card

export interface PeakCardText { name: string; elevation: string | null; listName: string | null; listProgress: string | null }

/** "Lugnaquilla" over "925 m · Vandeleur-Lynams 15/282", each clause dropped when unknown. */
export function peakCardText(peak: FlyoverPeakConfig, formatElevation: (m: number) => string): PeakCardText {
  const progress = peak.listBaggedCount != null && peak.listPeaksCount ? `${peak.listBaggedCount}/${peak.listPeaksCount}` : null;
  return {
    name: peak.name || "",
    elevation: peak.elevationMeters != null ? formatElevation(peak.elevationMeters) : null,
    listName: progress ? peak.listName || null : null,
    listProgress: progress
  };
}

// ---------------------------------------------------------------- where the card goes

/** Which side of its summit a card sits. Its pointer is on the opposite edge, aimed at the summit. */
export type CardSide = "above" | "right" | "left" | "below";
/** Tried in this order; on a tie the earlier wins, so an unobstructed card sits above as before. */
export const CARD_SIDES: CardSide[] = ["above", "right", "left", "below"];

export interface Pt { x: number; y: number }
export interface Rect { x: number; y: number; w: number; h: number }
export interface Bounds { left: number; top: number; right: number; bottom: number }

/** The card's box for a side: `gap` clears the pin (and holds the pointer) between summit and card. */
export function cardRect(side: CardSide, summit: Pt, w: number, h: number, gap: number): Rect {
  switch (side) {
    case "above": return { x: summit.x - w / 2, y: summit.y - gap - h, w, h };
    case "below": return { x: summit.x - w / 2, y: summit.y + gap, w, h };
    case "left": return { x: summit.x - gap - w, y: summit.y - h / 2, w, h };
    case "right": return { x: summit.x + gap, y: summit.y - h / 2, w, h };
  }
}

/** How much of the on-screen route a box would cover: samples along every segment that land in it. */
export function routeCoverage(route: Pt[], rect: Rect, pad = 0, step = 6): number {
  const x0 = rect.x - pad, y0 = rect.y - pad, x1 = rect.x + rect.w + pad, y1 = rect.y + rect.h + pad;
  let hits = 0;
  for (let i = 0; i < route.length - 1; i++) {
    const a = route[i], b = route[i + 1];
    if (!Number.isFinite(a.x) || !Number.isFinite(b.x)) continue;
    // Cheap reject: the segment's box misses the rect entirely.
    if (Math.max(a.x, b.x) < x0 || Math.min(a.x, b.x) > x1 || Math.max(a.y, b.y) < y0 || Math.min(a.y, b.y) > y1) continue;
    const n = Math.max(1, Math.ceil(Math.hypot(b.x - a.x, b.y - a.y) / step));
    for (let k = 0; k <= n; k++) {
      const x = a.x + ((b.x - a.x) * k) / n, y = a.y + ((b.y - a.y) * k) / n;
      if (x >= x0 && x <= x1 && y >= y0 && y <= y1) hits++;
    }
  }
  return hits;
}

/** Fraction (0..1) of a box that falls outside the usable part of the frame. */
export function outsideFraction(rect: Rect, bounds: Bounds): number {
  const ix = Math.max(0, Math.min(rect.x + rect.w, bounds.right) - Math.max(rect.x, bounds.left));
  const iy = Math.max(0, Math.min(rect.y + rect.h, bounds.bottom) - Math.max(rect.y, bounds.top));
  const area = rect.w * rect.h;
  return area > 0 ? 1 - (ix * iy) / area : 1;
}

/**
 * The side that hides the least of the flyover's line. The follow camera looks along the route,
 * so a card straight above the summit tends to sit right on the stretch about to be flown. A side
 * that would push the card under the logo, the stats panel or off the frame loses to any side that
 * stays clear of them.
 */
export function chooseCardSide(summit: Pt, w: number, h: number, gap: number, route: Pt[], bounds: Bounds, pad = 10): CardSide {
  let best: CardSide = "above";
  let bestScore = Infinity;
  for (const side of CARD_SIDES) {
    const rect = cardRect(side, summit, w, h, gap);
    const score = outsideFraction(rect, bounds) * 1e6 + routeCoverage(route, rect, pad);
    if (score < bestScore) { best = side; bestScore = score; }
  }
  return best;
}
